import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { tokenManager } from '@/lib/auth/token-manager';
import { AdminAccountRequiredError, authService, type AuthUser } from './auth-service';
import { AuthContext, type AuthContextType } from './auth-context';
import { logError } from '@/lib/logging';
import { ApiHttpError } from '@/http/api-client';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const userData = await authService.getMe();
      setUser(userData);
      return userData;
    } catch (error) {
      if (
        error instanceof AdminAccountRequiredError ||
        (error instanceof ApiHttpError && error.statusCode === 403)
      ) {
        tokenManager.clearToken();
        setUser(null);
        return null;
      }
      logError(error, { operation: 'fetchUser' });
      throw error;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (tokenManager.isAuthenticated()) {
          await fetchUser();
        }
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    void initAuth();
  }, [fetchUser]);

  const login = useCallback(
    async (token: string) => {
      tokenManager.setToken(token);
      const admin = await fetchUser();
      if (admin === null) {
        tokenManager.clearToken();
        throw new Error('An active platform admin account is required');
      }
    },
    [fetchUser]
  );

  const logout = useCallback(() => {
    tokenManager.clearToken();
    setUser(null);
  }, []);

  const devLogin = useCallback((token: string, mockUser: AuthUser) => {
    tokenManager.setToken(token);
    setUser(mockUser);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
    fetchUser,
    devLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
